/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains all the expert asynchronous routes used in the application.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to allow for the use of function component.
 */
import { lazy } from 'react';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to load components asychronously.
 */
import { IRoute } from '..';

/*
 * Used to get access to the API types and requests
 */
import * as FaceMatchDtos from '../../dtos/FaceMatch.dtos';

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

/**
 * Asynchronous expert page wrapping component
 */
export const AsyncExpertWrapperComponent = lazy(() => import('../../pages/expert/ExpertWrapperPage/ConnectedExpertWrapperPage'));

/**
 * Asynchronous expert dashboard page component
 */
export const AsyncDashboardPage = lazy(() => import('../../pages/expert/DashboardPage/ConnectedDashboardPage'));

/**
 * Asynchronous parent patient page component
 */
export const AsyncPatientPage = lazy(() => import('../../pages/expert/PatientPage/ConnectedPatientPage'));

/**
 * Asynchronous parent patient page component
 */
export const AsyncReferencePatientPage = lazy(() => import('../../pages/expert/ReferencePatientPage/ConnectedReferencePatientPage'));

/**
 * Asynchronous parent patient page component
 */
export const AsyncPatientSummaryPage = lazy(() => import('../../pages/expert/PatientSummaryPage/ConnectedPatientSummaryPage'));

/**
 * Asynchronous parent patient page component
 */
export const AsyncPatientArchivePage = lazy(() => import('../../pages/expert/PatientArchivePage/ConnectedPatientArchivePage'));

/**
 * Asynchronous parent patient page component
 */
export const AsyncPatientAnalysisArchivePage = lazy(() => import('../../pages/expert/PatientAnalysisArchivePage/ConnectedPatientAnalysisArchivePage'));

/**
 * Asynchronous expert profile page component
 */
export const AsyncProfilePage = lazy(() => import('../../pages/expert/ProfilePage/ConnectedProfilePage'));
export const AsyncMmeMatchesPage = lazy(() => import('../../pages/expert/MmeMatchesPage/MmeMatchesPage'));
export const AsyncFmcApplicationsPage = lazy(() => import('../../pages/expert/FmcApplicationsPage/FmcApplicationsPage'));
export const AsyncFmcApplicationReviewPage = lazy(() => import('../../pages/expert/FmcApplicationReviewPage/FmcApplicationReviewPage'));
export const AsyncExpertReversePhenotypingPage = lazy(() => import('../../pages/expert/ReversePhenotypingPage/ReversePhenotypingPage'));
const expertRoles = [FaceMatchDtos.Role.Expert, FaceMatchDtos.Role.SuperUser];
const expertRoute: IRoute = {
  component: AsyncExpertWrapperComponent,
  path: '/expert',
  requiresAuthentication: true,
  requiredRoles: [FaceMatchDtos.Role.Expert, FaceMatchDtos.Role.SuperUser, FaceMatchDtos.Role.Administrator],
  childRoutes: {
    reversePhenotyping: {
      component: AsyncExpertReversePhenotypingPage,
      path: '/expert/reverse-phenotyping',
      requiresAuthentication: true,
      requiredRoles: expertRoles
    },
    consortiumApplication: {
      component: AsyncFmcApplicationReviewPage,
      path: '/expert/consortium/applications/:applicationId',
      exact: true
    },
    consortium: {
      component: AsyncFmcApplicationsPage,
      path: '/expert/consortium',
      exact: true
    },
    mmeMatches: {
      component: AsyncMmeMatchesPage,
      path: '/expert/mme-matches/:studyNumber?',
      requiresAuthentication: true,
      requiredRoles: expertRoles
    },
    referencePatient: {
      component: AsyncReferencePatientPage,
      path: '/expert/reference-patient/:studyNumber',
      requiresAuthentication: true,
      requiredRoles: expertRoles
    },
    analysisArchive: {
      component: AsyncPatientAnalysisArchivePage,
      path: '/expert/patient/:studyNumber/archive/analysis',
      requiresAuthentication: true,
      requiredRoles: expertRoles
    },
    patientArchive: {
      component: AsyncPatientArchivePage,
      path: '/expert/patient/:studyNumber/archive/matches',
      requiresAuthentication: true,
      requiredRoles: expertRoles
    },
    patientInfo: {
      component: AsyncPatientPage,
      path: '/expert/patient/:studyNumber/information',
      requiresAuthentication: true,
      requiredRoles: expertRoles
    },
    patient: {
      component: AsyncPatientSummaryPage,
      path: '/expert/patient/:studyNumber',
      requiresAuthentication: true,
      requiredRoles: expertRoles
    },
    profile: {
      component: AsyncProfilePage,
      path: '/expert/profile',
      requiresAuthentication: true,
      requiredRoles: expertRoles
    },
    dashboard: {
      component: AsyncDashboardPage,
      path: '/expert',
      requiresAuthentication: true,
      requiredRoles: expertRoles
    }
  }
};
export default expertRoute;